import React, { useState, useEffect, useRef } from 'react';
import { Volume2, VolumeX, Music } from 'lucide-react';
import AdminPanel from './AdminPanel';
import { db, auth } from './firebase';
import { collection, getDocs, addDoc, query, orderBy, limit, doc, getDoc } from 'firebase/firestore';

// Sound Configuration
const SOUNDS = {
  bgm: ['/sounds/bgm1.mp3', '/sounds/bgm2.mp3', '/sounds/bgm3.mp3'],
  correct: '/sounds/correct.mp3',
  wrong: '/sounds/wrong.mp3',
  gameover: '/sounds/gameover.mp3',
  buttonHover: '/sounds/button-mouseover.mp3',
  buttonClick: '/sounds/button-click.mp3'
};

// Theme Assets
const THEME = {
  bgMenu: '/themes/magic/bg-mainmenu.png',
  bgCave: '/themes/magic/bg-cave.png',
  bgClouds: '/themes/magic/bg-clouds.png',
  bgForest: '/themes/magic/bg-forest.png',
  logo: '/themes/magic/logo.png',
  chooseIcon: '/themes/magic/choose-icon.png',
  mainButton: '/themes/magic/main-button-blue.png',
  categoryVocab: '/themes/magic/category-vocab.png',
  categoryGrammar: '/themes/magic/category-grammar.png',
  categorySpelling: '/themes/magic/category-spelling.png',
  leaderboardIcon: '/themes/magic/leaderboard-icon.png',
  unicornGameover: '/themes/magic/unicorn-gameover.png',
  questionBanner: '/themes/magic/question-banner.png',
  timerFrame: '/themes/magic/timer-frame.png',
  timerBar: '/themes/magic/timer-bar.png',
  timerEmpty: '/themes/magic/timer-empty.png',
  scorePanel: '/themes/magic/score-panel.png',
  streakPanel: '/themes/magic/streak-panel.png',
  buttons: {
    red: { active: '/themes/magic/button-red-active.png', inactive: '/themes/magic/button-red-inactive.png' },
    blue: { active: '/themes/magic/button-blue-active.png', inactive: '/themes/magic/button-blue-inactive.png' },
    yellow: { active: '/themes/magic/button-yellow-active.png', inactive: '/themes/magic/button-yellow-inactive.png' },
    green: { active: '/themes/magic/button-green-active.png', inactive: '/themes/magic/button-green-inactive.png' },
    grey: '/themes/magic/button-grey.png'
  },
  unicorn: {
    idle: '/themes/magic/unicorn-idle.png',
    sad: '/themes/magic/unicorn-sad.png',
    worried: '/themes/magic/unicorn-worried.png',
    correct: '/themes/magic/unicorn-correct.png',
    wrong: '/themes/magic/unicorn-wrong.png',
    gameover: '/themes/magic/unicorn-gameover.png'
  }
};

// Default Game Configuration
const GAME_CONFIG = {
  BASE_TIMER: 20,
  TIME_PER_CORRECT: 4,
  TIME_PENALTY_WRONG: 3,
  TIMER_CAP: 20,
  DRAIN_ACCELERATION: 1.15,
  ACCELERATION_INTERVAL: 20,
  POINTS_BASE: 100,
  SPEED_BONUS_FAST: 50,
  SPEED_BONUS_MEDIUM: 30,
  SPEED_BONUS_SLOW: 10,
  STREAK_5_MULTIPLIER: 1.2,
  STREAK_10_MULTIPLIER: 1.5,
  STREAK_15_MULTIPLIER: 2.0,
  STREAK_5_BONUS: 2,
  STREAK_10_BONUS: 4,
  STREAK_15_BONUS: 6,
  ALLOW_RETRY_AFTER_WRONG: true,
  WRONG_ANSWER_DELAY: 1.0,
  MAX_NAME_LENGTH: 20,
};

function QuizGame() {
  // Game State
  const [screen, setScreen] = useState('menu');
  const [category, setCategory] = useState(null);
  const [gameConfig, setGameConfig] = useState(GAME_CONFIG);
  const [questionsDatabase, setQuestionsDatabase] = useState({});
  const [loadingData, setLoadingData] = useState(true);
  const [timeLeft, setTimeLeft] = useState(GAME_CONFIG.BASE_TIMER);
  const [score, setScore] = useState(0);
  const [streak, setStreak] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [shuffledAnswers, setShuffledAnswers] = useState([]);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [gameTimeElapsed, setGameTimeElapsed] = useState(0);
  const [drainMultiplier, setDrainMultiplier] = useState(1);
  const [feedback, setFeedback] = useState(null);
  const [streakNotification, setStreakNotification] = useState(null);
  const [wrongAnswers, setWrongAnswers] = useState([]);
  const [pendingWrongAnswer, setPendingWrongAnswer] = useState(null); // NEW: For delay feedback
  const [showQuitModal, setShowQuitModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [scoreSaved, setScoreSaved] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [holdTimer, setHoldTimer] = useState(null);
  const [unicornFeedback, setUnicornFeedback] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [leaderboardFilter, setLeaderboardFilter] = useState('all');
  const [leaderboardPage, setLeaderboardPage] = useState(0);
  const [playerName, setPlayerName] = useState('');
  const [showNameInput, setShowNameInput] = useState(false);
  
  // Spelling Mode State
  const [jumbledWord, setJumbledWord] = useState('');
  const [userInput, setUserInput] = useState([]);
  const [correctAnswer, setCorrectAnswer] = useState('');
  const [spellingFeedback, setSpellingFeedback] = useState(null);
  
  // Audio State
  const [bgmVolume, setBgmVolume] = useState(0.3);
  const [sfxVolume, setSfxVolume] = useState(0.5);
  const [showBgmSlider, setShowBgmSlider] = useState(false);
  const [showSfxSlider, setShowSfxSlider] = useState(false);

  // Refs
  const timerInterval = useRef(null);
  const gameTimeInterval = useRef(null);
  const bgmRef = useRef(null);
  const sfxRef = useRef(null);
  const bgmSliderRef = useRef(null);
  const sfxSliderRef = useRef(null);

  // Audio Functions
  const bgmVolumeRef = useRef(bgmVolume); // Ref to track current volume for rotation
  
  // Keep ref in sync with state
  useEffect(() => {
    bgmVolumeRef.current = bgmVolume;
  }, [bgmVolume]);
  
  const playBgm = (volume) => {
    if (bgmRef.current) bgmRef.current.pause();
    const vol = volume !== undefined ? volume : bgmVolumeRef.current;
    if (vol === 0) return;
    const randomBgm = SOUNDS.bgm[Math.floor(Math.random() * SOUNDS.bgm.length)];
    bgmRef.current = new Audio(randomBgm);
    bgmRef.current.volume = vol;
    bgmRef.current.loop = false;
    bgmRef.current.onended = () => playBgm(); // No argument - will use current ref value
    bgmRef.current.play().catch(() => {});
  };

  const playSfx = (sound) => {
    if (sfxVolume === 0) return;
    sfxRef.current = new Audio(sound);
    sfxRef.current.volume = sfxVolume;
    sfxRef.current.play().catch(() => {});
  };

  const playButtonHover = () => {
    if (sfxVolume === 0) return;
    const audio = new Audio(SOUNDS.buttonHover);
    audio.volume = sfxVolume * 0.5;
    audio.play().catch(() => {});
  };

  const playButtonClick = () => {
    if (sfxVolume === 0) return;
    const audio = new Audio(SOUNDS.buttonClick);
    audio.volume = sfxVolume;
    audio.play().catch(() => {});
  };

  // Audio Initialization
  useEffect(() => {
    const startBgm = () => {
      if (bgmVolume > 0) playBgm(bgmVolume);
      document.removeEventListener('click', startBgm);
    };
    document.addEventListener('click', startBgm);
    return () => {
      document.removeEventListener('click', startBgm);
      if (bgmRef.current) bgmRef.current.pause();
    };
  }, []);

  useEffect(() => {
    if (bgmRef.current) {
      bgmRef.current.volume = bgmVolume;
      if (bgmVolume === 0) bgmRef.current.pause();
      else if (bgmRef.current.paused) bgmRef.current.play().catch(() => {});
    } else if (bgmVolume > 0) {
      playBgm(bgmVolume);
    }
  }, [bgmVolume]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (bgmSliderRef.current && !bgmSliderRef.current.contains(event.target)) setShowBgmSlider(false);
      if (sfxSliderRef.current && !sfxSliderRef.current.contains(event.target)) setShowSfxSlider(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Audio Controls Component
  const renderAudioControls = (showQuit = false) => (
    <div className="fixed top-4 right-4 flex gap-2 z-40">
      {showQuit && (
        <button onClick={() => { playButtonClick(); setShowQuitModal(true); }}
          className="bg-red-500 hover:bg-red-600 text-white font-bold py-4 px-6 rounded-lg transition"
          style={{ fontSize: 'clamp(0.875rem, 1.2vw, 1.125rem)' }}>Quit</button>
      )}
      <div className="relative" ref={bgmSliderRef}>
        <button onClick={() => { playButtonClick(); setShowBgmSlider(!showBgmSlider); setShowSfxSlider(false); }}
          className={`${bgmVolume === 0 ? 'bg-gray-500' : 'bg-purple-500 hover:bg-purple-600'} text-white font-bold p-4 rounded-lg transition`}
          title="Music Volume">
          <Music size={28} className={bgmVolume === 0 ? 'opacity-50' : ''} />
        </button>
        {showBgmSlider && (
          <div className="absolute top-full mt-2 right-0 bg-white rounded-lg shadow-lg p-4" style={{ minWidth: '160px' }} onMouseDown={(e) => e.stopPropagation()}>
            <p className="text-sm text-gray-600 mb-3 font-bold">Music</p>
            <input type="range" min="0" max="100" value={Math.round(bgmVolume * 100)}
              onChange={(e) => setBgmVolume(parseInt(e.target.value, 10) / 100)}
              className="w-full h-2 bg-gray-200 rounded-lg cursor-pointer" style={{ accentColor: '#9333ea' }} />
            <p className="text-sm text-center text-gray-500 mt-2">{Math.round(bgmVolume * 100)}%</p>
          </div>
        )}
      </div>
      <div className="relative" ref={sfxSliderRef}>
        <button onClick={() => { playButtonClick(); setShowSfxSlider(!showSfxSlider); setShowBgmSlider(false); }}
          className={`${sfxVolume === 0 ? 'bg-gray-500' : 'bg-blue-500 hover:bg-blue-600'} text-white font-bold p-4 rounded-lg transition`}
          title="Sound Effects Volume">
          {sfxVolume === 0 ? <VolumeX size={28} /> : <Volume2 size={28} />}
        </button>
        {showSfxSlider && (
          <div className="absolute top-full mt-2 right-0 bg-white rounded-lg shadow-lg p-4" style={{ minWidth: '160px' }} onMouseDown={(e) => e.stopPropagation()}>
            <p className="text-sm text-gray-600 mb-3 font-bold">Sound FX</p>
            <input type="range" min="0" max="100" value={Math.round(sfxVolume * 100)}
              onChange={(e) => setSfxVolume(parseInt(e.target.value, 10) / 100)}
              className="w-full h-2 bg-gray-200 rounded-lg cursor-pointer" style={{ accentColor: '#3b82f6' }} />
            <p className="text-sm text-center text-gray-500 mt-2">{Math.round(sfxVolume * 100)}%</p>
          </div>
        )}
      </div>
    </div>
  );

  // Helper function to get unique highest scores per name
  const getUniqueLeaderboard = (entries) => {
    const bestScores = new Map();
    entries.forEach(entry => {
      const normalizedName = entry.name.toUpperCase().trim();
      const existing = bestScores.get(normalizedName);
      if (!existing || entry.score > existing.score) {
        bestScores.set(normalizedName, entry);
      }
    });
    return Array.from(bestScores.values()).sort((a, b) => b.score - a.score);
  };

  // Data Loading
  const loadGameData = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'settings', 'config'));
      if (settingsDoc.exists()) setGameConfig({ ...GAME_CONFIG, ...settingsDoc.data() });
      
      const questionsSnapshot = await getDocs(collection(db, 'questions'));
      const loadedQuestions = { grammar: [], vocab: [], spelling: [] };
      questionsSnapshot.forEach((doc) => {
        const question = { id: doc.id, ...doc.data() };
        const cat = question.category?.toLowerCase();
        if (loadedQuestions[cat]) loadedQuestions[cat].push(question);
      });
      setQuestionsDatabase(loadedQuestions);

      const leaderboardQuery = query(collection(db, 'leaderboard'), orderBy('score', 'desc'), limit(200));
      const leaderboardSnapshot = await getDocs(leaderboardQuery);
      const loadedLeaderboard = [];
      leaderboardSnapshot.forEach((doc) => loadedLeaderboard.push({ id: doc.id, ...doc.data() }));
      setLeaderboard(loadedLeaderboard);
      setLoadingData(false);
    } catch (error) {
      setQuestionsDatabase({});
      setLoadingData(false);
    }
  };
    
  useEffect(() => { loadGameData(); }, []);
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => setIsAdminLoggedIn(!!user));
    return () => unsubscribe();
  }, []);

  // Unicorn State Helper
  const getUnicornImage = () => {
    if (unicornFeedback === 'correct') return THEME.unicorn.correct;
    if (unicornFeedback === 'wrong') return THEME.unicorn.wrong;
    const timerPercentage = (timeLeft / gameConfig.TIMER_CAP) * 100;
    if (timerPercentage > 50) return THEME.unicorn.idle;
    if (timerPercentage > 25) return THEME.unicorn.sad;
    return THEME.unicorn.worried;
  };

  // Utility Functions
  const shuffleArray = (array) => {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  };

  const jumbleWord = (word) => {
    if (!word || word.length <= 1) return word;
    const letters = word.toLowerCase().split('');
    for (let i = letters.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [letters[i], letters[j]] = [letters[j], letters[i]];
    }
    const jumbled = letters.join('');
    if (jumbled === word.toLowerCase() && word.length > 2) return jumbleWord(word);
    return jumbled;
  };

  // Question Loading
  const loadNewQuestion = () => {
    const questions = questionsDatabase[category];
    if (!questions || questions.length === 0) return;
    
    const randomQuestion = questions[Math.floor(Math.random() * questions.length)];
    if (category === 'spelling') {
      const correctWord = randomQuestion.answers[0];
      const jumbled = jumbleWord(correctWord);
      const questionWithJumbled = randomQuestion.question.replace('_', jumbled);
      setCorrectAnswer(correctWord.toLowerCase());
      setJumbledWord(jumbled);
      setUserInput(new Array(correctWord.length).fill(''));
      setCurrentQuestion({ ...randomQuestion, question: questionWithJumbled });
      setSpellingFeedback(null);
    } else {
      const answersWithIndices = randomQuestion.answers.map((answer, index) => ({ text: answer, originalIndex: index }));
      setCurrentQuestion(randomQuestion);
      setShuffledAnswers(shuffleArray(answersWithIndices));
    }
    setQuestionStartTime(Date.now());
    setFeedback(null);
    setWrongAnswers([]);
    setPendingWrongAnswer(null);
  };

  // Scoring Functions
  const calculateSpeedBonus = (timeInSeconds) => {
    if (timeInSeconds < 3) return gameConfig.SPEED_BONUS_FAST;
    if (timeInSeconds < 5) return gameConfig.SPEED_BONUS_MEDIUM;
    if (timeInSeconds < 8) return gameConfig.SPEED_BONUS_SLOW;
    return 0;
  };

  const getStreakMultiplier = () => {
    if (streak >= 15) return gameConfig.STREAK_15_MULTIPLIER;
    if (streak >= 10) return gameConfig.STREAK_10_MULTIPLIER;
    if (streak >= 5) return gameConfig.STREAK_5_MULTIPLIER;
    return 1;
  };

  const checkStreakMilestone = (newStreak) => {
    if (newStreak === 5 || newStreak === 10 || newStreak === 15) {
      const bonusTime = newStreak === 5 ? gameConfig.STREAK_5_BONUS : newStreak === 10 ? gameConfig.STREAK_10_BONUS : gameConfig.STREAK_15_BONUS;
      const multiplier = newStreak === 5 ? gameConfig.STREAK_5_MULTIPLIER : newStreak === 10 ? gameConfig.STREAK_10_MULTIPLIER : gameConfig.STREAK_15_MULTIPLIER;
      const bonusPoints = Math.round(gameConfig.POINTS_BASE * multiplier);
      setStreakNotification({ streak: newStreak, bonusPoints });
      setTimeout(() => setStreakNotification(null), 2000);
      return bonusTime;
    }
    return 0;
  };

  // Answer Handlers
  const handleAnswer = (selectedAnswer, answerIndex) => {
    // Ignore clicks if there's a pending wrong answer being shown
    if (pendingWrongAnswer !== null) return;
    
    const isCorrect = selectedAnswer.originalIndex === currentQuestion.correct;
    const timeSpent = (Date.now() - questionStartTime) / 1000;
    
    if (isCorrect) {
      const speedBonus = calculateSpeedBonus(timeSpent);
      const streakMultiplier = getStreakMultiplier();
      const earnedPoints = Math.round((gameConfig.POINTS_BASE + speedBonus) * streakMultiplier);
      setScore(prev => prev + earnedPoints);
      const newStreak = streak + 1;
      setStreak(newStreak);
      const streakBonus = checkStreakMilestone(newStreak);
      setTimeLeft(prev => Math.min(prev + gameConfig.TIME_PER_CORRECT + streakBonus, gameConfig.TIMER_CAP));
      playSfx(SOUNDS.correct);
      setUnicornFeedback('correct');
      setTimeout(() => setUnicornFeedback(null), 500);
      loadNewQuestion();
    } else {
      setStreak(0);
      setTimeLeft(prev => Math.max(prev - gameConfig.TIME_PENALTY_WRONG, 0));
      playSfx(SOUNDS.wrong);
      setUnicornFeedback('wrong');
      setTimeout(() => setUnicornFeedback(null), 500);
      
      if (gameConfig.ALLOW_RETRY_AFTER_WRONG) {
        // Show feedback first, then grey out after delay
        setPendingWrongAnswer(answerIndex);
        const delay = (gameConfig.WRONG_ANSWER_DELAY || 1.0) * 1000;
        setTimeout(() => {
          setWrongAnswers(prev => [...prev, answerIndex]);
          setPendingWrongAnswer(null);
        }, delay);
      } else {
        // No retry - show feedback briefly then move to next question
        setPendingWrongAnswer(answerIndex);
        const delay = (gameConfig.WRONG_ANSWER_DELAY || 1.0) * 1000;
        setTimeout(() => {
          setPendingWrongAnswer(null);
          loadNewQuestion();
        }, delay);
      }
    }
  };

  const handleSpellingSubmit = () => {
    const userAnswer = userInput.join('').toLowerCase();
    const isCorrect = userAnswer === correctAnswer;
    if (isCorrect) {
      setScore(prev => prev + 1);
      playSfx(SOUNDS.correct);
    } else {
      playSfx(SOUNDS.wrong);
    }
    loadNewQuestion();
  };

  const handleSpellingSkip = () => {
    playButtonClick();
    setTimeLeft(prev => Math.max(prev - gameConfig.TIME_PENALTY_WRONG, 0));
    loadNewQuestion();
  };

  const handleSpellingInput = (e) => {
    const key = e.key;
    if (key === 'Enter') {
      if (!userInput.some(char => char === '')) handleSpellingSubmit();
      return;
    }
    if (key === 'Backspace') {
      setUserInput(prev => {
        const newInput = [...prev];
        for (let i = newInput.length - 1; i >= 0; i--) {
          if (newInput[i] !== '') { newInput[i] = ''; break; }
        }
        return newInput;
      });
      return;
    }
    if (key.length === 1 && /[a-zA-Z]/.test(key)) {
      setUserInput(prev => {
        const newInput = [...prev];
        const emptyIndex = newInput.findIndex(char => char === '');
        if (emptyIndex !== -1) newInput[emptyIndex] = key.toLowerCase();
        return newInput;
      });
    }
  };

  // Game Flow
  const startGame = (selectedCategory) => {
    setCategory(selectedCategory);
    setScreen('game');
    setTimeLeft(gameConfig.BASE_TIMER);
    setScore(0);
    setStreak(0);
    setGameTimeElapsed(0);
    setDrainMultiplier(1);
    setScoreSaved(false);
    setWrongAnswers([]);
    setPendingWrongAnswer(null);
    setUnicornFeedback(null);
    
    const questions = questionsDatabase[selectedCategory];
    if (!questions || questions.length === 0) return;
    
    const firstQuestion = questions[Math.floor(Math.random() * questions.length)];
    if (selectedCategory === 'spelling') {
      const correctWord = firstQuestion.answers[0];
      const jumbled = jumbleWord(correctWord);
      setCorrectAnswer(correctWord.toLowerCase());
      setJumbledWord(jumbled);
      setUserInput(new Array(correctWord.length).fill(''));
      setCurrentQuestion({ ...firstQuestion, question: firstQuestion.question.replace('_', jumbled) });
      setSpellingFeedback(null);
    } else {
      setCurrentQuestion(firstQuestion);
      setShuffledAnswers(shuffleArray(firstQuestion.answers.map((answer, index) => ({ text: answer, originalIndex: index }))));
    }
    setQuestionStartTime(Date.now());
  };

  const endGame = () => {
    setScreen('gameover');
    setShowNameInput(false);
    setPlayerName('');
    clearInterval(timerInterval.current);
    clearInterval(gameTimeInterval.current);
    playSfx(SOUNDS.gameover);
  };

  const handleQuitGame = () => {
    clearInterval(timerInterval.current);
    clearInterval(gameTimeInterval.current);
    setShowQuitModal(false);
    setScreen('menu');
  };

  // Score Saving
  const saveScore = async () => {
    if (score === 0) { setSuccessMessage('Score must be greater than 0 to save!'); setTimeout(() => setSuccessMessage(null), 2000); return; }
    if (!playerName.trim()) { setSuccessMessage('Please enter your name!'); setTimeout(() => setSuccessMessage(null), 2000); return; }
    if (scoreSaved) { setSuccessMessage('Score already saved!'); setTimeout(() => setSuccessMessage(null), 2000); return; }
    
    try {
      const newEntry = { name: playerName.trim().toUpperCase(), score, category, date: new Date().toISOString(), timestamp: Date.now() };
      await addDoc(collection(db, 'leaderboard'), newEntry);
      setLeaderboard([...leaderboard, newEntry].sort((a, b) => b.score - a.score).slice(0, 200));
      setScoreSaved(true);
      setSuccessMessage('Score saved successfully!');
      setTimeout(() => setSuccessMessage(null), 2000);
    } catch (error) {
      setSuccessMessage('Error saving score. Try again.');
      setTimeout(() => setSuccessMessage(null), 2000);
    }
  };

  // Timer Effects
  useEffect(() => {
    if (screen === 'game') {
      timerInterval.current = setInterval(() => {
        setTimeLeft(prev => {
          const newTime = prev - (0.1 * drainMultiplier);
          if (newTime <= 0) { endGame(); return 0; }
          return newTime;
        });
      }, 100);
      return () => clearInterval(timerInterval.current);
    }
  }, [screen, drainMultiplier]);

  useEffect(() => {
    if (screen === 'game') {
      gameTimeInterval.current = setInterval(() => {
        setGameTimeElapsed(prev => {
          const newTime = prev + 1;
          if (newTime % gameConfig.ACCELERATION_INTERVAL === 0) {
            setDrainMultiplier(prevMultiplier => prevMultiplier * gameConfig.DRAIN_ACCELERATION);
          }
          return newTime;
        });
      }, 1000);
      return () => clearInterval(gameTimeInterval.current);
    }
  }, [screen, gameConfig]);

  useEffect(() => {
    if (screen === 'game' && category === 'spelling') {
      window.addEventListener('keydown', handleSpellingInput);
      return () => window.removeEventListener('keydown', handleSpellingInput);
    }
  }, [screen, category, userInput, spellingFeedback]);

  useEffect(() => { if (screen === 'leaderboard') loadGameData(); }, [screen]);

  // Loading Screen
  if (loadingData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Loading Game...</h2>
          <p className="text-gray-600">Please wait while we load questions</p>
          <div className="mt-4 animate-pulse"><div className="h-2 bg-purple-200 rounded"></div></div>
        </div>
      </div>
    );
  }

  // Admin Panel
  if (showAdminPanel) {
    return <AdminPanel onClose={() => setShowAdminPanel(false)} gameConfig={gameConfig}
      onConfigUpdate={(newConfig) => setGameConfig(newConfig)}
      onLoginStateChange={(loggedIn) => setIsAdminLoggedIn(loggedIn)}
      onQuestionsUpdated={loadGameData} />;
  }

  // Menu Screen
  if (screen === 'menu' || screen === 'category') {
    const showCategories = screen === 'category';
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden"
        style={{ backgroundColor: '#1a1025', backgroundImage: `url(${THEME.bgMenu})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        {renderAudioControls()}
        <div className="flex flex-col items-center" style={{ position: 'absolute', top: '50%', transform: 'translateY(-50%)' }}>
          {/* Logo */}
          <div className="select-none" style={{ marginBottom: 'clamp(16px, 3vh, 40px)' }}
            onMouseDown={() => { if (showCategories) return; if (isAdminLoggedIn) setShowAdminPanel(true); else { const timer = setTimeout(() => setShowAdminPanel(true), 3000); setHoldTimer(timer); } }}
            onMouseUp={() => { if (holdTimer) clearTimeout(holdTimer); }}
            onMouseLeave={() => { if (holdTimer) clearTimeout(holdTimer); }}
            onTouchStart={() => { if (showCategories) return; if (isAdminLoggedIn) setShowAdminPanel(true); else { const timer = setTimeout(() => setShowAdminPanel(true), 3000); setHoldTimer(timer); } }}
            onTouchEnd={() => { if (holdTimer) clearTimeout(holdTimer); }}>
            <img src={showCategories ? THEME.chooseIcon : THEME.logo} alt="Word Quest"
              style={{ width: 'clamp(200px, 40vw, 600px)', height: 'auto', filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.5)) drop-shadow(0 2px 4px rgba(0,0,0,0.3))' }} />
            {isAdminLoggedIn && !showCategories && <span className="block text-center text-sm text-purple-300 font-bold">[ADMIN]</span>}
          </div>

          {/* Menu Buttons */}
          <div className="flex flex-col items-center" style={{ gap: 'clamp(8px, 1.5vh, 20px)' }}>
            {!showCategories ? (
              <>
                <button onClick={() => { playButtonClick(); setScreen('category'); }} onMouseEnter={playButtonHover}
                  className="relative group transition-transform duration-200 hover:scale-105" style={{ width: 'clamp(180px, 28vw, 350px)' }}>
                  <img src={THEME.mainButton} alt="" className="w-full h-auto" style={{ filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.5))' }} />
                  <span className="absolute inset-0 flex items-center justify-center font-bold text-white pointer-events-none"
                    style={{ fontSize: 'clamp(1rem, 2.5vw, 2rem)', textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>Play</span>
                </button>
                <button onClick={() => { playButtonClick(); setLeaderboardFilter('all'); setScreen('leaderboard'); }} onMouseEnter={playButtonHover}
                  className="relative group transition-transform duration-200 hover:scale-105" style={{ width: 'clamp(180px, 28vw, 350px)' }}>
                  <img src={THEME.mainButton} alt="" className="w-full h-auto" style={{ filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.5))' }} />
                  <span className="absolute inset-0 flex items-center justify-center font-bold text-white pointer-events-none"
                    style={{ fontSize: 'clamp(1rem, 2.5vw, 2rem)', textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>Leaderboard</span>
                </button>
              </>
            ) : (
              <>
                {/* Category Buttons */}
                <div className="flex items-center justify-center" style={{ gap: 'clamp(16px, 3vw, 32px)', marginBottom: 'clamp(16px, 3vh, 32px)' }}>
                  <button onClick={() => { playButtonClick(); startGame('grammar'); }} onMouseEnter={playButtonHover}
                    className="transition-transform duration-200 hover:scale-110" style={{ width: 'clamp(220px, 32vw, 380px)' }}>
                    <img src={THEME.categoryGrammar} alt="Grammar" className="w-full h-auto" style={{ filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.5))' }} />
                  </button>
                  <button onClick={() => { playButtonClick(); startGame('vocab'); }} onMouseEnter={playButtonHover}
                    className="transition-transform duration-200 hover:scale-110" style={{ width: 'clamp(220px, 32vw, 380px)' }}>
                    <img src={THEME.categoryVocab} alt="Vocabulary" className="w-full h-auto" style={{ filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.5))' }} />
                  </button>
                  <button onClick={() => { playButtonClick(); startGame('spelling'); }} onMouseEnter={playButtonHover}
                    className="transition-transform duration-200 hover:scale-110" style={{ width: 'clamp(220px, 32vw, 380px)' }}>
                    <img src={THEME.categorySpelling} alt="Spelling" className="w-full h-auto" style={{ filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.5))' }} />
                  </button>
                </div>
                {/* Back Button */}
                <button onClick={() => { playButtonClick(); setScreen('menu'); }} onMouseEnter={playButtonHover}
                  className="relative group transition-transform duration-200 hover:scale-105" style={{ width: 'clamp(150px, 20vw, 280px)' }}>
                  <img src={THEME.mainButton} alt="" className="w-full h-auto" style={{ filter: 'drop-shadow(0 6px 12px rgba(0,0,0,0.5))' }} />
                  <span className="absolute inset-0 flex items-center justify-center font-bold text-white pointer-events-none"
                    style={{ fontSize: 'clamp(1rem, 2vw, 1.75rem)', textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}>← Back</span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Game Screen
  if (screen === 'game' && currentQuestion) {
    const timerPercentage = (timeLeft / gameConfig.TIMER_CAP) * 100;

    // Spelling Mode
    if (category === 'spelling') {
      return (
        <div className="min-h-screen flex items-center justify-center p-4"
          style={{ backgroundColor: '#1a1025', backgroundImage: `url(${THEME.bgForest})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
          {showQuitModal && (
            <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
                <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">Quit Game?</h3>
                <p className="text-gray-600 mb-6 text-center">Your progress will not be saved!</p>
                <div className="flex gap-4">
                  <button onClick={() => { playButtonClick(); setShowQuitModal(false); }} onMouseEnter={playButtonHover}
                    className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-xl transition">Cancel</button>
                  <button onClick={() => { playButtonClick(); handleQuitGame(); }} onMouseEnter={playButtonHover}
                    className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-6 rounded-xl transition">Quit</button>
                </div>
              </div>
            </div>
          )}
          {renderAudioControls(true)}
          <div className={`bg-white rounded-2xl shadow-2xl p-8 w-full transition-all duration-300 ${
            spellingFeedback === 'correct' ? 'ring-4 ring-green-500 shadow-green-500/50' :
            spellingFeedback === 'wrong' ? 'ring-4 ring-red-500 shadow-red-500/50' : ''
          }`} style={{ maxWidth: '800px' }}>
            <div className="flex justify-between items-center mb-6">
              <div className="text-2xl font-bold text-gray-800">Score: {score}</div>
              <div className="text-xl font-bold text-gray-600">⏱️ Time: {Math.floor(timeLeft)}s</div>
            </div>
            <div className="mb-8">
              <p className="text-2xl text-gray-800 text-center leading-relaxed">
                {currentQuestion.question.split(' ').map((word, i) => {
                  if (word.toLowerCase() === jumbledWord.toLowerCase()) {
                    return <span key={i} className="font-bold underline decoration-2 text-purple-600">{word}{' '}</span>;
                  }
                  return <span key={i}>{word} </span>;
                })}
              </p>
            </div>
            <div className="mb-6">
              <p className="text-lg text-gray-600 text-center mb-4">Type the correct spelling:</p>
              <div className="flex justify-center gap-2 flex-wrap mb-6">
                {userInput.map((letter, index) => (
                  <div key={index} className="w-12 h-16 border-4 border-gray-300 rounded-lg flex items-center justify-center text-2xl font-bold bg-white">
                    {letter.toUpperCase() || '_'}
                  </div>
                ))}
              </div>
            </div>
            {spellingFeedback === 'wrong' && (
              <div className="text-center mb-4">
                <p className="text-red-600 font-bold text-xl">❌ Incorrect!</p>
                <p className="text-gray-700 text-lg">Correct spelling: <span className="font-bold text-green-600">{correctAnswer}</span></p>
              </div>
            )}
            {spellingFeedback === 'correct' && <div className="text-center mb-4"><p className="text-green-600 font-bold text-xl">✅ Correct!</p></div>}
            <div className="flex justify-center gap-4">
              <button onClick={handleSpellingSubmit} onMouseEnter={playButtonHover} disabled={userInput.some(char => char === '')}
                className={`font-bold py-3 px-8 rounded-xl transition ${userInput.some(char => char === '') ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600 text-white'}`}>
                Submit (Enter)
              </button>
              <button onClick={handleSpellingSkip} onMouseEnter={playButtonHover}
                className="font-bold py-3 px-8 rounded-xl transition bg-orange-500 hover:bg-orange-600 text-white">Skip →</button>
            </div>
            <div className="mt-4 text-center text-sm text-gray-500">Use keyboard to type • Backspace to delete • Enter to submit</div>
          </div>
        </div>
      );
    }

    // Grammar/Vocab Mode
    const buttonColorMap = ['red', 'blue', 'yellow', 'green'];
    const gameBackground = category === 'grammar' ? THEME.bgClouds : THEME.bgCave;
    
    return (
      <div className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: '#1a1025', backgroundImage: `url(${gameBackground})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        {streakNotification && (
          <div className="fixed top-20 left-1/2 transform -translate-x-1/2 bg-yellow-400 text-white px-6 py-3 rounded-full font-bold shadow-lg animate-bounce z-50"
            style={{ fontSize: 'clamp(0.875rem, 1.5vw, 1.125rem)' }}>
            🔥 {streakNotification.streak} STREAK! +{streakNotification.bonusPoints} POINTS
          </div>
        )}
        {showQuitModal && (
          <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
              <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">Quit Game?</h3>
              <p className="text-gray-600 mb-6 text-center">Your progress will not be saved!</p>
              <div className="flex gap-4">
                <button onClick={() => { playButtonClick(); setShowQuitModal(false); }} onMouseEnter={playButtonHover}
                  className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-bold py-3 px-6 rounded-xl transition">Cancel</button>
                <button onClick={() => { playButtonClick(); handleQuitGame(); }} onMouseEnter={playButtonHover}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-6 rounded-xl transition">Quit</button>
              </div>
            </div>
          </div>
        )}
        {renderAudioControls(true)}
        <div className="flex items-start justify-center" style={{ gap: 'clamp(16px, 2vw, 32px)', padding: 'clamp(16px, 3vw, 48px)', maxWidth: '1800px', width: '100%' }}>
          {/* Game Area */}
          <div className="flex flex-col" style={{ gap: 'clamp(8px, 1vw, 16px)', flex: '1', maxWidth: '1200px' }}>
            {/* Question Banner */}
            <div className="relative w-full flex items-center justify-center"
              style={{ backgroundImage: `url(${THEME.questionBanner})`, backgroundSize: '100% 100%', backgroundRepeat: 'no-repeat', backgroundPosition: 'center', aspectRatio: '4/1', padding: '0 10%', overflow: 'hidden' }}>
              <p className="text-white text-center font-bold"
                style={{ fontSize: 'clamp(1rem, 2.5vw, 2rem)', textShadow: '0 4px 8px rgba(0,0,0,0.7), 0 0 20px rgba(0,0,0,0.5)', lineHeight: '1.2', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {currentQuestion.question}
              </p>
            </div>
            {/* Timer Bar */}
            <div className="relative" style={{ width: '100%', aspectRatio: '10/1' }}>
              <div className="absolute" style={{ top: '18%', bottom: '18%', left: '6.7%', right: '6.7%', backgroundColor: '#1f1f2e', borderRadius: '4px' }} />
              <div className="absolute" style={{ top: '18%', bottom: '18%', left: '6.7%', right: '6.7%', background: 'linear-gradient(to right, #3b82f6, #60a5fa)', borderRadius: '4px', transformOrigin: 'left center', transform: `scaleX(${timerPercentage / 100})`, transition: 'transform 0.1s linear' }} />
              <img src={THEME.timerFrame} alt="" className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 2, objectFit: 'fill' }} />
            </div>
            {/* Answer Buttons */}
            <div className="grid grid-cols-2 w-full" style={{ gap: 'clamp(4px, 0.5vw, 12px)' }}>
              {shuffledAnswers.map((answer, index) => {
                const isWrong = wrongAnswers.includes(index);
                const isPendingWrong = pendingWrongAnswer === index;
                const colorKey = buttonColorMap[index];
                const isDisabled = isWrong || pendingWrongAnswer !== null;
                
                return (
                  <button key={index} onClick={() => !isDisabled && handleAnswer(answer, index)} onMouseEnter={() => !isDisabled && playButtonHover()}
                    disabled={isDisabled} 
                    className={`relative group transition-all duration-200 ${isPendingWrong ? 'animate-shake' : ''}`}
                    style={{ cursor: isDisabled ? 'not-allowed' : 'pointer', aspectRatio: '2.7/1' }}>
                    <img src={isWrong ? THEME.buttons.grey : THEME.buttons[colorKey].inactive} alt=""
                      className={`w-full h-full object-fill ${!isDisabled ? 'group-hover:hidden' : ''}`}
                      style={{ 
                        filter: isWrong ? 'grayscale(100%) brightness(0.7)' : 
                                isPendingWrong ? 'brightness(0.7) sepia(100%) saturate(300%) hue-rotate(-10deg)' : 
                                'drop-shadow(0 8px 16px rgba(0,0,0,0.3))',
                        transition: 'filter 0.2s ease'
                      }} />
                    {!isDisabled && (
                      <img src={THEME.buttons[colorKey].active} alt=""
                        className="w-full h-full object-fill hidden group-hover:block absolute inset-0"
                        style={{ filter: 'drop-shadow(0 12px 24px rgba(0,0,0,0.4)) drop-shadow(0 0 30px rgba(255,255,255,0.3))' }} />
                    )}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <span className={`font-bold text-center px-4 ${isPendingWrong ? 'text-red-200' : 'text-white'}`}
                        style={{ fontSize: 'clamp(1rem, 2.5vw, 2rem)', textShadow: '0 3px 8px rgba(0,0,0,0.9), 0 0 20px rgba(0,0,0,0.5)', maxWidth: '90%', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {isPendingWrong ? '✗ ' : ''}{answer.text}
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Status Panel */}
          <div className="flex flex-col items-center" style={{ gap: 'clamp(8px, 1vw, 16px)', width: 'clamp(150px, 18vw, 280px)' }}>
            {/* Unicorn */}
            <div className="relative">
              <img src={getUnicornImage()} alt="Unicorn" className="object-contain transition-all duration-300 relative"
                style={{ width: '100%', aspectRatio: '1/1', zIndex: 2 }} />
              <div className="absolute left-1/2 transform -translate-x-1/2"
                style={{ bottom: '5px', width: '75%', height: '20px', background: 'radial-gradient(ellipse, rgba(0,0,0,0.5) 0%, transparent 70%)', borderRadius: '50%', zIndex: 1 }} />
            </div>
            {/* Score Panel */}
            <div className="relative w-full"
              style={{ backgroundImage: `url(${THEME.scorePanel})`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center', aspectRatio: '2.5/1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span className="font-bold text-white" style={{ fontSize: 'clamp(1.25rem, 3vw, 2.25rem)', textShadow: '0 2px 4px rgba(0,0,0,0.6)' }}>{score}</span>
            </div>
            {/* Streak Panel */}
            <div className="relative w-full"
              style={{ backgroundImage: `url(${THEME.streakPanel})`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundPosition: 'center', aspectRatio: '2.5/1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <span className="font-bold text-white" style={{ fontSize: 'clamp(1.25rem, 3vw, 2.25rem)', textShadow: '0 2px 4px rgba(0,0,0,0.6)' }}>{streak}</span>
            </div>
          </div>
        </div>
        {/* Shake animation CSS */}
        <style>{`
          @keyframes shake {
            0%, 100% { transform: translateX(0); }
            20% { transform: translateX(-8px); }
            40% { transform: translateX(8px); }
            60% { transform: translateX(-6px); }
            80% { transform: translateX(6px); }
          }
          .animate-shake {
            animation: shake 0.4s ease-in-out;
          }
        `}</style>
      </div>
    );
  }

  // Game Over Screen
  if (screen === 'gameover') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: '#1a1025', backgroundImage: `url(${THEME.bgMenu})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="relative">
          {successMessage && (
            <div className="absolute -top-20 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-6 py-3 rounded-lg font-bold shadow-lg animate-pulse whitespace-nowrap">
              {successMessage}
            </div>
          )}
          <div className="bg-white rounded-2xl shadow-2xl" style={{ width: 'clamp(300px, 45vw, 500px)', padding: 'clamp(16px, 3vw, 32px)' }}>
            <div className="flex flex-col items-center">
              <div className="relative">
                <img src={THEME.unicornGameover} alt="Game Over Unicorn" className="relative"
                  style={{ width: 'clamp(100px, 22vw, 190px)', height: 'auto', marginBottom: '-10px', zIndex: 2 }} />
                <div className="absolute left-1/2 transform -translate-x-1/2"
                  style={{ bottom: '0px', width: '70%', height: '18px', background: 'radial-gradient(ellipse, rgba(0,0,0,0.4) 0%, transparent 70%)', borderRadius: '50%', zIndex: 1 }} />
              </div>
              <h2 className="font-bold text-center text-gray-800" style={{ fontSize: 'clamp(2.2rem, 5vw, 3.75rem)', marginBottom: 'clamp(8px, 1.5vh, 16px)' }}>GAME OVER</h2>
            </div>
            <div className="text-center" style={{ marginBottom: 'clamp(12px, 2vh, 24px)' }}>
              <p className="font-bold text-purple-600" style={{ fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', marginBottom: 'clamp(4px, 0.5vh, 8px)' }}>{score}</p>
              <p className="text-gray-600" style={{ fontSize: 'clamp(1rem, 2vw, 1.5rem)' }}>Final Score</p>
            </div>
            {showNameInput && !scoreSaved ? (
              <div className="space-y-4">
                <input type="text" placeholder="Enter your name" maxLength={gameConfig.MAX_NAME_LENGTH} value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl text-center text-2xl font-bold uppercase" />
                <button onClick={() => { playButtonClick(); saveScore(); }} onMouseEnter={playButtonHover}
                  className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-xl transition">Save Score</button>
                <button onClick={() => { playButtonClick(); setShowNameInput(false); }} onMouseEnter={playButtonHover}
                  className="w-full bg-gray-400 hover:bg-gray-500 text-white font-bold py-2 px-6 rounded-xl transition">Back</button>
              </div>
            ) : (
              <div className="space-y-4">
                {!scoreSaved && (
                  <button onClick={() => { playButtonClick(); setShowNameInput(true); }} onMouseEnter={playButtonHover} disabled={score === 0}
                    className={`w-full font-bold py-4 px-6 rounded-xl transition ${score === 0 ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600 text-white transform hover:scale-105'}`}>
                    {score === 0 ? 'Cannot Save Score of 0' : 'Save to Leaderboard'}
                  </button>
                )}
                <button onClick={() => { playButtonClick(); setScreen('category'); }} onMouseEnter={playButtonHover}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-4 px-6 rounded-xl transition transform hover:scale-105">Play Again</button>
                <button onClick={() => { playButtonClick(); setScreen('menu'); }} onMouseEnter={playButtonHover}
                  className="w-full bg-red-500 hover:bg-red-600 text-white font-bold py-3 px-6 rounded-xl transition">Main Menu</button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Leaderboard Screen
  if (screen === 'leaderboard') {
    // Filter by category first, then get unique names with highest scores
    const categoryFiltered = leaderboardFilter === 'all' ? leaderboard : leaderboard.filter(entry => entry.category === leaderboardFilter);
    const filteredLeaderboard = getUniqueLeaderboard(categoryFiltered);
    
    const ITEMS_PER_PAGE = 5;
    const MAX_PAGES = 5;
    const totalPages = Math.min(Math.ceil(filteredLeaderboard.length / ITEMS_PER_PAGE), MAX_PAGES);
    const startIndex = leaderboardPage * ITEMS_PER_PAGE;
    const displayLeaderboard = filteredLeaderboard.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    const handleFilterChange = (filter) => { setLeaderboardFilter(filter); setLeaderboardPage(0); };

    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4"
        style={{ backgroundColor: '#1a1025', backgroundImage: `url(${THEME.bgMenu})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        {/* Leaderboard Icon */}
        <img src={THEME.leaderboardIcon} alt="Leaderboard"
          style={{ width: 'clamp(280px, 45vw, 450px)', height: 'auto', marginBottom: 'clamp(10px, -8vh, -40px)', zIndex: 10, position: 'relative', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' }} />
        <div className="bg-white rounded-2xl shadow-2xl"
          style={{ width: 'clamp(320px, 45vw, 500px)', padding: 'clamp(16px, 3vw, 32px)', paddingTop: 'clamp(24px, 4vw, 40px)' }}>
          {/* Filter Tabs */}
          <div className="flex" style={{ gap: 'clamp(4px, 0.5vw, 8px)', marginBottom: 'clamp(12px, 2vh, 24px)' }}>
            {['all', 'vocab', 'grammar', 'spelling'].map(filter => (
              <button key={filter} onClick={() => { playButtonClick(); handleFilterChange(filter); }} onMouseEnter={playButtonHover}
                className={`flex-1 rounded-lg font-bold transition ${
                  leaderboardFilter === filter 
                    ? filter === 'grammar' ? 'bg-blue-600 text-white' : filter === 'spelling' ? 'bg-green-600 text-white' : 'bg-purple-600 text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
                style={{ padding: 'clamp(6px, 1vh, 12px) clamp(8px, 1vw, 16px)', fontSize: 'clamp(0.7rem, 1.2vw, 1rem)' }}>
                {filter === 'all' ? 'Overall' : filter.charAt(0).toUpperCase() + filter.slice(1)}
              </button>
            ))}
          </div>
          {/* Leaderboard Entries */}
          <div style={{ marginBottom: 'clamp(12px, 2vh, 24px)' }}>
            <div className="flex flex-col" style={{ gap: 'clamp(4px, 0.8vh, 10px)' }}>
              {[0, 1, 2, 3, 4].map((slotIndex) => {
                const entry = displayLeaderboard[slotIndex];
                const globalIndex = startIndex + slotIndex;
                if (!entry) {
                  return (
                    <div key={slotIndex} className="flex items-center justify-between rounded-xl bg-gray-50 opacity-50"
                      style={{ height: 'clamp(45px, 8vh, 65px)', padding: 'clamp(8px, 1.5vh, 16px) clamp(12px, 1.5vw, 20px)' }}>
                      <span className="text-gray-400">-</span>
                    </div>
                  );
                }
                return (
                  <div key={slotIndex}
                    className={`flex items-center justify-between rounded-xl ${
                      globalIndex === 0 ? 'bg-yellow-100 border-2 border-yellow-400' :
                      globalIndex === 1 ? 'bg-gray-100 border-2 border-gray-400' :
                      globalIndex === 2 ? 'bg-orange-100 border-2 border-orange-400' : 'bg-gray-50'
                    }`}
                    style={{ height: 'clamp(45px, 8vh, 65px)', padding: 'clamp(8px, 1.5vh, 16px) clamp(12px, 1.5vw, 20px)' }}>
                    <div className="flex items-center" style={{ gap: 'clamp(8px, 1vw, 16px)' }}>
                      <span className="font-bold text-gray-700" style={{ fontSize: 'clamp(1.25rem, 2vw, 2rem)', width: 'clamp(24px, 3vw, 40px)' }}>{globalIndex + 1}</span>
                      <div>
                        <span className="font-bold block" style={{ fontSize: 'clamp(0.875rem, 1.5vw, 1.25rem)' }}>{entry.name}</span>
                        <span className="text-gray-500" style={{ fontSize: 'clamp(0.625rem, 1vw, 0.875rem)' }}>{entry.category}</span>
                      </div>
                    </div>
                    <span className="font-bold text-purple-600" style={{ fontSize: 'clamp(1rem, 1.8vw, 1.5rem)' }}>{entry.score}</span>
                  </div>
                );
              })}
            </div>
          </div>
          {/* Pagination */}
          <div className="flex items-center justify-center" style={{ gap: 'clamp(12px, 2vw, 24px)', marginBottom: 'clamp(12px, 2vh, 20px)' }}>
            <button onClick={() => { playButtonClick(); setLeaderboardPage(prev => Math.max(0, prev - 1)); }} onMouseEnter={playButtonHover}
              disabled={leaderboardPage === 0}
              className={`rounded-lg font-bold transition ${leaderboardPage === 0 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-purple-500 hover:bg-purple-600 text-white'}`}
              style={{ padding: 'clamp(6px, 1vh, 12px) clamp(10px, 1.5vw, 16px)', fontSize: 'clamp(0.875rem, 1.5vw, 1.25rem)' }}>◀</button>
            <span className="text-gray-600 font-bold" style={{ fontSize: 'clamp(0.875rem, 1.5vw, 1.25rem)' }}>
              {totalPages > 0 ? `${leaderboardPage + 1} / ${totalPages}` : '0 / 0'}
            </span>
            <button onClick={() => { playButtonClick(); setLeaderboardPage(prev => Math.min(totalPages - 1, prev + 1)); }} onMouseEnter={playButtonHover}
              disabled={leaderboardPage >= totalPages - 1}
              className={`rounded-lg font-bold transition ${leaderboardPage >= totalPages - 1 ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-purple-500 hover:bg-purple-600 text-white'}`}
              style={{ padding: 'clamp(6px, 1vh, 12px) clamp(10px, 1.5vw, 16px)', fontSize: 'clamp(0.875rem, 1.5vw, 1.25rem)' }}>▶</button>
          </div>
          {/* Back Button */}
          <button onClick={() => { playButtonClick(); setLeaderboardPage(0); setScreen('menu'); }} onMouseEnter={playButtonHover}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-xl transition"
            style={{ padding: 'clamp(10px, 1.5vh, 16px) clamp(16px, 2vw, 24px)', fontSize: 'clamp(0.875rem, 1.5vw, 1.25rem)' }}>Back to Menu</button>
        </div>
      </div>
    );
  }

  return null;
}

export default QuizGame;